import { Modal, Skeleton, Badge, Spin, Pagination } from 'antd';
import dayjs from 'dayjs';
import { formatAmountNoSymbol, formatDayJsDate } from '../../../utils';

interface BucketInvoice {
  id: number;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string;
  invoiceAmount: number;
  invoiceAmountInr?: number | null;
  currency: string | null;
  sapDocumentNumber: string;
  status: string;
  statusLabel: string;
  statusColor: string;
  agingDays: number;
}

interface BucketInvoiceDetailsModalProps {
  open: boolean;
  onClose: () => void;
  onAfterOpenChange?: (open: boolean) => void;
  isLoading: boolean;
  error?: any;
  data:
    | {
        customer: {
          customerId: number;
          customerNumber: string;
          customerName: string;
          customerEmail: string;
        };
        bucket: {
          bucketCode: string;
          bucketLabel: string;
          fromDays: number;
          toDays: number | null;
        };
        invoices: BucketInvoice[];
        totals: {
          totalInvoiceAmount: number;
          totalInvoiceAmountInr?: number;
          invoiceCount: number;
        };
        meta: {
          total: number;
          page: number;
          limit: number;
          totalPages: number;
        };
      }
    | undefined;
  currentPage: number;
  currentPageSize: number;
  onPageChange: (page: number, pageSize: number) => void;
}

export default function BucketInvoiceDetailsModal({
  open,
  onClose,
  onAfterOpenChange,
  isLoading,
  error,
  data,
  currentPage,
  currentPageSize,
  onPageChange,
}: BucketInvoiceDetailsModalProps) {
  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <div className="h-8 w-1 bg-gradient-to-b from-indigo-500 via-blue-500 to-purple-500 rounded-full"></div>
          <div>
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Invoice Details
            </div>
            <div className="text-sm font-semibold text-gray-900">
              {data?.bucket?.bucketLabel || ''}
            </div>
          </div>
        </div>
      }
      open={open}
      onCancel={onClose}
      afterOpenChange={onAfterOpenChange}
      footer={null}
      width={900}
      className="bucket-invoice-modal"
    >
      {isLoading ? (
        <div className="py-12 flex flex-col items-center justify-center min-h-[300px]">
          <Spin size="large" className="mb-4" />
          <div className="text-sm text-gray-600 font-medium mb-4">
            Loading invoice details...
          </div>
          <Skeleton active paragraph={{ rows: 8 }} className="w-full" />
        </div>
      ) : error ? (
        <div className="py-8 text-center">
          <div className="text-red-600 text-sm font-medium mb-2">
            Error loading invoice details
          </div>
          <div className="text-gray-500 text-xs">{JSON.stringify(error)}</div>
        </div>
      ) : !data ? (
        <div className="py-8 text-center text-gray-500 text-sm">
          No data available
        </div>
      ) : (
        <div className="py-2">
          {/* Customer Info */}
          <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
            <div className="grid grid-cols-3 gap-4 text-xs">
              <div>
                <span className="text-gray-500 font-medium">Customer:</span>
                <div className="text-gray-900 font-semibold mt-0.5">
                  {data?.customer?.customerName || '-'}
                </div>
                <div className="text-gray-600 text-[11px] mt-0.5">
                  {data?.customer?.customerNumber || '-'}
                </div>
              </div>
              <div>
                <span className="text-gray-500 font-medium">Bucket:</span>
                <div className="text-gray-900 font-semibold mt-0.5">
                  {data?.bucket?.bucketLabel || '-'}
                </div>
                {data?.bucket?.fromDays !== null &&
                  data?.bucket?.toDays !== null &&
                  !['NOT_DUE', 'GETTING_DUE'].includes(
                    data?.bucket?.bucketCode,
                  ) && (
                    <div className="text-gray-600 text-[11px] mt-0.5">
                      {data?.bucket?.fromDays} - {data?.bucket?.toDays} days
                    </div>
                  )}
              </div>
              <div>
                <span className="text-gray-500 font-medium">
                  Total Invoices:
                </span>
                <div className="text-gray-900 font-semibold mt-0.5">
                  {data?.totals?.invoiceCount || 0}
                </div>
              </div>
            </div>
          </div>

          {/* Totals Summary */}
          <div className="mb-3 grid grid-cols-2 gap-2 p-2 bg-blue-50 rounded border border-blue-100">
            <div className="text-center">
              <div className="text-[10px] text-gray-600 font-medium uppercase tracking-wide">
                Total Invoice Amount (INR)
              </div>
              <div className="text-sm font-bold text-primary-600 mt-0.5">
                {formatAmountNoSymbol(
                  (data?.totals as any)?.totalInvoiceAmountInr ??
                    (data?.totals as any)?.totalInvoiceAmount ??
                    (data?.totals as any)?.totalGrossAmount ??
                    0,
                )}
              </div>
            </div>
            <div className="text-center border-l border-blue-200 pl-2">
              <div className="text-[10px] text-gray-600 font-medium uppercase tracking-wide">
                Count
              </div>
              <div className="text-sm font-bold text-gray-900 mt-0.5">
                {data?.totals?.invoiceCount || 0}
              </div>
            </div>
          </div>

          {/* Invoice Table */}
          <div className="border border-gray-200 rounded-lg overflow-hidden flex flex-col">
            <div
              className="overflow-x-auto"
              style={{ maxHeight: 'calc(100vh - 25rem)', overflowY: 'auto' }}
            >
              <table className="w-full text-xs">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-2 py-1.5 text-left font-semibold text-gray-700 text-[11px] uppercase tracking-wide">
                      Invoice #
                    </th>
                    <th className="px-2 py-1.5 text-left font-semibold text-gray-700 text-[11px] uppercase tracking-wide">
                      SAP Doc
                    </th>
                    <th className="px-2 py-1.5 text-left font-semibold text-gray-700 text-[11px] uppercase tracking-wide">
                      Invoice Date
                    </th>
                    <th className="px-2 py-1.5 text-left font-semibold text-gray-700 text-[11px] uppercase tracking-wide">
                      Due Date
                    </th>
                    <th className="px-2 py-1.5 text-right font-semibold text-gray-700 text-[11px] uppercase tracking-wide">
                      Invoice Amount (INR)
                    </th>
                    <th className="px-2 py-1.5 text-center font-semibold text-gray-700 text-[11px] uppercase tracking-wide">
                      Aging Days
                    </th>
                    <th className="px-2 py-1.5 text-center font-semibold text-gray-700 text-[11px] uppercase tracking-wide">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {!data?.invoices || data.invoices.length === 0 ? (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-4 py-6 text-center text-gray-500 text-xs"
                      >
                        No invoices found
                      </td>
                    </tr>
                  ) : (
                    data.invoices.map(invoice => (
                      <tr
                        key={invoice.id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-2 py-1.5 font-medium text-gray-900">
                          {invoice.invoiceNumber}
                        </td>
                        <td className="px-2 py-1.5 text-gray-600">
                          {invoice.sapDocumentNumber || '-'}
                        </td>
                        <td className="px-2 py-1.5 text-gray-600">
                          {formatDayJsDate(
                            dayjs(invoice.invoiceDate),
                            'DD MMM YY',
                          )}
                        </td>
                        <td className="px-2 py-1.5 text-gray-600">
                          {formatDayJsDate(dayjs(invoice.dueDate), 'DD MMM YY')}
                        </td>
                        <td className="px-2 py-1.5 text-right font-semibold text-gray-900">
                          {invoice.invoiceAmountInr != null
                            ? formatAmountNoSymbol(invoice.invoiceAmountInr)
                            : '-'}
                        </td>
                        <td className="px-2 py-1.5 text-center text-gray-600">
                          {Math.abs(invoice.agingDays)}
                        </td>
                        <td className="px-2 py-1.5 text-center">
                          <Badge
                            color={invoice.statusColor}
                            text={invoice.statusLabel}
                            className="text-[10px]"
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {data?.meta && data.meta.totalPages > 0 && (
            <div
              className="flex justify-between bg-gray-50 items-center px-4 py-2.5 border-t border-gray-200 mt-3"
              style={{ position: 'relative', zIndex: 50 }}
            >
              <Pagination
                size="small"
                total={data.meta.total}
                pageSize={currentPageSize}
                current={currentPage}
                pageSizeOptions={[25, 50, 100, 200]}
                showSizeChanger
                showTotal={total => (
                  <span className="text-xs text-gray-600 font-semibold">
                    Total {total} items
                  </span>
                )}
                onChange={(page, pageSize) => {
                  onPageChange(page, pageSize);
                }}
                onShowSizeChange={(current, size) => {
                  onPageChange(1, size);
                }}
                className="text-sm"
                getPopupContainer={triggerNode =>
                  triggerNode.parentElement || document.body
                }
              />
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
